import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Star, Video, Languages, IndianRupee } from "lucide-react";
import { wellnessApi } from "@/lib/api";
import { SAMPLE_THERAPISTS, normalizeConsultant } from "@/lib/therapists";

export default function WellnessTherapist() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const sample = [...SAMPLE_THERAPISTS].find((x) => x.id === id);
    if (sample) { setT(sample); setLoading(false); return; }
    wellnessApi.consultant(id)
      .then((res) => { const c = res.data?.consultant || res.data; setT(c ? normalizeConsultant(c) : SAMPLE_THERAPISTS[0]); })
      .catch(() => setT(SAMPLE_THERAPISTS[0]))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading || !t) return <p className="text-center text-muted-foreground py-16">Loading…</p>;

  const modes = [t.offers_video && "Video", t.offers_audio && "Audio", t.offers_in_person && "In-clinic"].filter(Boolean).join(" · ");

  return (
    <div className="space-y-5 pb-24">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm font-medium" style={{ color: "#7C3AED" }} data-testid="profile-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="rounded-3xl overflow-hidden" style={{ background: "#EDE7F8" }}>
        <img src={t.profile_photo} alt={t.full_name} className="w-full h-72 object-cover" />
      </div>

      <div className="flex items-start gap-3">
        <div className="flex-1">
          <h1 className="text-2xl font-bold" style={{ color: "#1F1147" }}>{t.full_name}</h1>
          <p className="font-semibold mt-1" style={{ color: "#7C3AED" }}>{t.title}</p>
        </div>
        <span className="flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-bold" style={{ background: "#FFF7E6", color: "#B45309" }}><Star className="h-3.5 w-3.5" style={{ color: "#F59E0B", fill: "#F59E0B" }} />{t.rating} ({t.reviews})</span>
      </div>

      <div className="flex items-center rounded-2xl py-4" style={{ background: "#F8F5FE" }}>
        {[[`${t.experience_years}+`, "Years Exp."], [`${t.sessions}+`, "Sessions"], [t.audience.split(" ")[0], t.audience]].map((s, i) => (
          <div key={i} className="flex-1 text-center px-2">
            <p className="text-base font-bold" style={{ color: "#2E1065" }}>{s[0]}</p>
            <p className="text-[11px] mt-0.5" style={{ color: "#8B7CA6" }}>{s[1]}</p>
          </div>
        ))}
      </div>

      <div>
        <h2 className="font-bold mb-2" style={{ color: "#1F1147" }}>About</h2>
        <p className="text-sm leading-relaxed" style={{ color: "#53456F" }}>{t.about}</p>
      </div>

      <div>
        <h2 className="font-bold mb-2" style={{ color: "#1F1147" }}>Specialisations</h2>
        <div className="flex flex-wrap gap-2">
          {t.specialisations.map((s) => <span key={s} className="rounded-full px-3 py-1.5 text-sm font-semibold" style={{ background: "#F3EEFC", color: "#7C3AED" }}>{s}</span>)}
        </div>
      </div>

      <div className="space-y-3">
        <InfoRow Icon={Video} label="Session type" value={modes || "Video"} />
        <InfoRow Icon={Languages} label="Languages" value={t.languages.join(", ")} />
        <InfoRow Icon={IndianRupee} label="Fees" value={`₹${t.fee.toLocaleString("en-IN")} per session (${t.session_mins} mins)`} />
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 md:static md:border-0 md:p-0" style={{ borderColor: "#F1ECFA" }}>
        <button onClick={() => navigate(`/mental-wellness/book/${t.id}`)} data-testid="book-a-session" className="w-full rounded-2xl py-4 font-bold text-white" style={{ background: "#7C3AED" }}>Book a Session</button>
      </div>
    </div>
  );
}

function InfoRow({ Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="h-4 w-4 shrink-0" style={{ color: "#7C3AED" }} />
      <span className="text-sm font-semibold w-24 shrink-0" style={{ color: "#8B7CA6" }}>{label}</span>
      <span className="text-sm font-semibold flex-1" style={{ color: "#1F1147" }}>{value}</span>
    </div>
  );
}
